import React, { memo, useContext, useState } from "react";
import { ObjectContext } from "@providers/ObjectProvider";
import Avatar from '@mui/joy/Avatar';
import Chip from '@mui/joy/Chip';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import ButtonGroup from '@mui/joy/ButtonGroup';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CardOverflow from '@mui/joy/CardOverflow';
import CardActions from '@mui/joy/CardActions';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import SvgIcon from '@mui/joy/SvgIcon';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import AddAlarmRoundedIcon from '@mui/icons-material/AddAlarmRounded';
import Tooltip from '@mui/joy/Tooltip';
import Table from '@mui/joy/Table';
import Stack from '@mui/joy/Stack';
import ObjectRelationshipCard from '@components/objects/ObjectRelationshipCard';
import { gradientTint } from "@utils/gradientTint";
import { sortByDate } from "@utils/sorters";
import ObjectPreview from "./ObjectPreview";
import { formatDate, isValidDate } from "@utils/dates";
import { dueDateClassForTask } from "@utils/dueDateClassForTask";
import { groupBy } from "@utils/groupBy";

interface ColumnWithValue {
  label: string;
  value: string;//|number|null;
  transformer?: never;
}
interface ColumnWithTransformer {
  label: string;
  value?: never;
  transformer: Function;
}

type IColumn = ColumnWithValue | ColumnWithTransformer;

const ObjectRelationshipInvolvements = memo(function ObjectRelationshipInvolvements({
  relationship = [],
  title,
  tint = null,
  // columns,
  collapsible = false,
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const perPage = 5;

  const groupedRelationship = groupBy(relationship, ({category_name}) => category_name);

  const sortedRelationship = Object.keys(groupedRelationship)
    .sort()
    .reduce((acc, key) => ({
        ...acc, [key]: groupedRelationship[key]
    }), {});

  const canCollapse = collapsible && relationship.length > perPage;
  const isEmpty = relationship.length === 0;

  return (
    <ObjectRelationshipCard
      unpadded
      empty={isEmpty}
      title={
        <CardOverflow
          sx={theme => ({
            borderTopLeftRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            borderTopRightRadius: 'calc(var(--Card-childRadius) + var(--variant-borderWidth))',
            marginBlockStart: 'var(--CardOverflow-offset)',
            ...gradientTint(theme, tint),
          })}
        >
          <CardActions
            sx={theme => ({
              py: 1.5,
              pointerEvents: canCollapse ? 'all' : 'none',
              cursor: 'pointer',
              userSelect: 'none',
              ':hover .pagination': {
                backgroundColor: '#FFFFFFB8',
              },
            })}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={0.5}
            >
              <Typography sx={theme => ({flex: 1, fontWeight: '600', color: 'white' })}>{title}</Typography>
            </Stack>
          </CardActions>
        </CardOverflow>
      }
    >
      {/* <CardOverflow> */}
      <Table
        stickyHeader
        stripe="odd"
        // hoverRow
        sx={{
          margin: 0,
          padding: 0,
          tableLayout: 'auto',
          'thead th': {
            borderRadius: '0 !important',
          },
        }}
      >

        {Object.entries(sortedRelationship).map(([category, involvements], index:number) => {
          return (<React.Fragment key={index}>
            <thead>
              <tr>
                <th colSpan={2}>{category}</th>
              </tr>
            </thead>
            <tbody>
              {involvements.map((involvement, involvementIndex:number) => {
                return (<React.Fragment key={involvementIndex}>
                  <tr>
                    <td>
                      <Tooltip title={involvement.description} placement="left">
                        <Typography fontSize={'sm'}>
                          {involvement.name}
                        </Typography>
                      </Tooltip>
                      {involvement.pivot.notes && <Typography fontSize={'sm'} sx={{fontSize: '0.75rem'}}>{involvement.pivot.notes}</Typography>}
                    </td>
                    <td>{involvement.pivot.date}</td>
                  </tr>
                  {/*
                  {involvement.pivot.notes && (
                    <tr>
                      <td colSpan={2}><Typography fontSize={'sm'}>{involvement.pivot.notes}</Typography></td>
                    </tr>
                  )}
                  */}
                </React.Fragment>)
              })}
            </tbody>
          </React.Fragment>);
        })}
      </Table>
      {/* </CardOverflow> */}

    </ObjectRelationshipCard>
  );
});

export default ObjectRelationshipInvolvements;
